import React from 'react';
import {createClient} from 'libs/api/invest/generated/createClient';
import {Query, QueryRequest} from 'libs/api/invest/generated/schema';


export const graphqlClient = createClient({
  fetcher: (query, fetch) => {
    if (!window.GLOBAL_CONFIG.CSRF || !window.GLOBAL_CONFIG.SESSION) {
      throw new Error('No csrf of session set')
    }

    let host = window.GLOBAL_CONFIG.GRAPHQL_URL;
    const hostUrl = new URL(host);

    // use the same sub-domain to make request
    host = host.replace(hostUrl.origin, window.location.origin);

    return fetch(
      host,
      {
        method: 'POST',
        body: JSON.stringify(query),
        headers: {
          'X-CSRFToken': window.GLOBAL_CONFIG.CSRF,
          'sessionid': window.GLOBAL_CONFIG.SESSION,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.json());
  }
})

type ThenArg<T> = T extends PromiseLike<infer U> ? U : T
type QueryResponse = ThenArg<ReturnType<typeof graphqlClient.query>>
type UseQueryReturnType = {
  errors: QueryResponse['errors']
  isFetching: boolean
  refetch: () => void
}

export function useQuery(query: QueryRequest): UseQueryReturnType & {data: Query}
export function useQuery<F extends keyof Query>(query: QueryRequest, field: F): UseQueryReturnType & {data: Query[F]}
export function useQuery<F extends keyof Query>(query: QueryRequest, field?: F) {
  type dataType = F extends keyof Query ? Query[F] : Query

  const [data, setData] = React.useState<dataType>();
  const [errors, setErrors] = React.useState<QueryResponse['errors']>();
  const [isFetching, setIsFetching] = React.useState(false);
  const [hasResponse, setHasResponse] = React.useState(false);

  const refetch = React.useReducer(() => {
    setHasResponse(false);
    setErrors(undefined);
  }, undefined)[1];

  React.useEffect(() => {
    if (!hasResponse && !isFetching) {
      setIsFetching(true);
      graphqlClient
        .query(query)
        .then(response => {
          setErrors(response.errors);
          let resData;
          if (field && response.data) {
            resData = (response.data as any)[field] as dataType;
          } else {
            resData = response.data as unknown as dataType;
          }
          response.data ? setData(resData) : setData(undefined);
          setHasResponse(true);
        })
        .finally(() => setIsFetching(false));
    }
  }, [hasResponse, isFetching])

  return {
    data,
    errors,
    isFetching,
    refetch,
  };
}
